/**
 * The study's trial phase for code that holds only the stored documents
 * (backlog e4, round 2, 2026-10-21): the protocol analysis, the project
 * document and, when it has it, Study Design's config. The SAME answer the
 * study profile gives (resolvePhaseFact, trialPhase.ts), so a generator that
 * reads this and one that reads the profile cannot disagree:
 *
 *   - Study Design's phase wins;
 *   - no investigational drug or device (product type other, from Study
 *     Settings on the project document or from the protocol) is
 *     "Not applicable";
 *   - the project document's `phase`, which is a lifecycle stage
 *     ("protocol_development"), is never the trial phase;
 *   - unknown is undefined, never a default such as "Phase 2".
 *
 * Generators must not read `analysis.studyDesign.phase` or
 * `projectData.phase` themselves; phaseReadersSweep.test.ts fails if one does.
 *
 * Pure.
 */
import { resolveProductType } from "./productFraming";
import { resolvePhaseFact, type PhaseFact } from "./trialPhase";
import type { StudyProfile } from "./types";

export interface StoredPhaseInputs {
  /** protocolAnalysis/latest.analysis */
  analysis?: unknown;
  /** projects/{id}: its studySetup (product type) and its lifecycle `phase` */
  projectData?: unknown;
  /** studyDesign/config, when the caller has it */
  studyDesign?: unknown;
  /** Study Settings, when the caller holds them apart from the project document */
  studySetup?: unknown;
}

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});

/** The resolved phase fact, with its evidence and any sponsor-review note. */
export function storedTrialPhaseFact(i: StoredPhaseInputs): PhaseFact {
  const project = obj(i.projectData);
  const studySetup = i.studySetup ?? project.studySetup;
  return resolvePhaseFact({
    studyDesignPhase: obj(i.studyDesign).phase,
    analysis: i.analysis,
    projectPhase: project.phase,
    productType: resolveProductType(studySetup, i.analysis).value,
  });
}

/** The resolved phase as text, or undefined when it is not known. */
export function storedTrialPhase(i: StoredPhaseInputs): string | undefined {
  return storedTrialPhaseFact(i).value ?? undefined;
}

/** The cheap path when the study profile is already built. */
export function profileTrialPhase(profile: Pick<StudyProfile, "identity"> | null | undefined): string | undefined {
  return profile?.identity?.phase?.value ?? undefined;
}
